// Wave 3 — FileCard decomposition: canvas/image tooling for signatures & stamps.
// Extracted verbatim from pages/FileCard.js (browser-only, not unit tested —
// jsdom has no real canvas; exercised in the browser via the preview flow).

export const SIG_PEN_COLOR = "#1d4ed8";

export const getImageSize = (dataUrl, kind = "image") =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.naturalWidth || img.width || 0,
        height: img.naturalHeight || img.height || 0,
      });
    img.onerror = () => reject(new Error(`Unable to decode saved ${kind} image.`));
    img.src = dataUrl;
  });

export const recolorSignatureToBlue = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("Unable to prepare signature canvas.");

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;

        for (let i = 0; i < px.length; i += 4) {
          const alpha = px[i + 3];
          if (alpha === 0) continue;

          // Preserve transparency and anti-aliased edges while forcing blue ink.
          px[i] = 29;
          px[i + 1] = 78;
          px[i + 2] = 216;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("Unable to process signature image."));
    img.src = dataUrl;
  });

export const fileToDataUrl = (rawFile) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read stamp image file."));
    reader.readAsDataURL(rawFile);
  });

export const dataUrlToBytes = async (dataUrl) => {
  const res = await fetch(dataUrl);
  return new Uint8Array(await res.arrayBuffer());
};

// Generic table-stamp generator. Title, column headers and row labels are all
// caller-supplied so any office can build its own table stamp.
export const createTableStampTemplateDataUrl = ({ title = "", columns = [], rows = [] } = {}) => {
  const canvas = document.createElement("canvas");
  const cols = columns.map((c) => String(c || "").trim()).filter(Boolean);
  const rowLabels = rows.map((r) => String(r || "").trim()).filter(Boolean);
  const titleH = 70;
  const headerH = 55;
  const rowH = 52;
  const dataRows = Math.max(rowLabels.length, 3);
  canvas.width = 1200;
  canvas.height = titleH + headerH + rowH * dataRows + 80;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Unable to generate stamp template.");

  const blue = "#1d4ed8";
  const left = 40;
  const top = 40;
  const width = 1120;
  const height = canvas.height - 80;
  const columnCount = Math.max(cols.length, 1);
  const bodyH = height - titleH - headerH;
  const bodyRowH = bodyH / dataRows;
  const colWidth = width / columnCount;
  const headerY = top + titleH;
  const bodyStartY = headerY + headerH;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = blue;
  ctx.fillStyle = blue;
  ctx.lineWidth = 3;

  // Outer border and main row lines.
  ctx.strokeRect(left, top, width, height);
  ctx.beginPath();
  ctx.moveTo(left, headerY);
  ctx.lineTo(left + width, headerY);
  ctx.moveTo(left, bodyStartY);
  ctx.lineTo(left + width, bodyStartY);
  ctx.stroke();

  // Vertical column separators.
  ctx.beginPath();
  for (let i = 1; i < columnCount; i += 1) {
    const x = left + colWidth * i;
    ctx.moveTo(x, headerY);
    ctx.lineTo(x, top + height);
  }
  ctx.stroke();

  // Body row separators.
  for (let i = 1; i < dataRows; i += 1) {
    const y = bodyStartY + bodyRowH * i;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(left + width, y);
    ctx.stroke();
  }

  // Text labels.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const safeTitle = String(title || "TABLE").toUpperCase().slice(0, 28);
  ctx.font = `bold ${safeTitle.length > 20 ? 44 : 54}px Arial`;
  ctx.fillText(safeTitle, left + width / 2, top + titleH / 2);

  ctx.font = `bold ${cols.some((c) => c.length > 14) ? 30 : 40}px Arial`;
  cols.forEach((col, index) => {
    ctx.fillText(col, left + colWidth * index + colWidth / 2, headerY + headerH / 2);
  });

  ctx.textAlign = "left";
  ctx.font = "34px Arial";
  rowLabels.forEach((label, index) => {
    const y = bodyStartY + bodyRowH * index + bodyRowH / 2;
    ctx.fillText(label, left + 14, y);
  });

  return canvas.toDataURL("image/png");
};

// Classic HQ KA CIS table — now just the generic builder with the CIS defaults.
export const createCisStampTemplateDataUrl = () =>
  createTableStampTemplateDataUrl({
    title: "HQ KA CIS",
    columns: ["APPOINTMENT", "SIGNATURE", "DATE"],
    rows: [
      "CO OPS", "COL CIS", "SO1 COMMS", "SO1 INFRA/IS", "SOII COMMS",
      "SOII CYBER SYS", "SO II ICT", "YOS", "EOS", "CLK",
    ],
  });

export const createRoleBoxStampTemplateDataUrl = (label, rank = "MAJ") => {
  const canvas = document.createElement("canvas");
  canvas.width = 1100;
  canvas.height = 420;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Unable to generate role stamp template.");

  const blue = "#1696d2";
  const left = 36;
  const top = 36;
  const width = 1028;
  const height = 348;
  const lineY = top + 160;
  const safeLabelRaw = String(label || "APPOINTMENT")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  const safeLabel =
    safeLabelRaw.length > 24 ? `${safeLabelRaw.slice(0, 24).trim()}...` : safeLabelRaw;
  const safeRank = String(rank || "").toUpperCase().trim();
  const rankWidthReserve = safeRank ? 210 : 40;
  const labelFontSize = safeLabel.length > 20 ? 52 : safeLabel.length > 16 ? 62 : 76;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = blue;
  ctx.fillStyle = blue;
  ctx.lineWidth = 6;

  ctx.strokeRect(left, top, width, height);

  // Dotted signature line.
  ctx.beginPath();
  ctx.lineWidth = 5;
  ctx.setLineDash([3, 13]);
  ctx.moveTo(left + 26, lineY);
  ctx.lineTo(left + width - rankWidthReserve, lineY);
  ctx.stroke();
  ctx.setLineDash([]);

  if (safeRank) {
    ctx.font = "bold 64px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(safeRank, left + width - 190, lineY - 4);
  }

  ctx.font = `bold ${labelFontSize}px Arial`;
  ctx.textAlign = "center";
  ctx.fillText(safeLabel, left + width / 2, top + height - 88);

  return canvas.toDataURL("image/png");
};
